import React, { useMemo, useState } from 'react';
import { Box, Stack, Typography, Select, MenuItem, Button, InputLabel, FormControl } from '@mui/material';

import {
  computeInstallmentPreview,
  MIN_INSTALLMENTS,
  MAX_INSTALLMENTS,
} from '../../utils/paymentPlanMath';

const currency = (n) =>
  Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const DEFAULT_INSTALLMENTS = 6;

const installmentOptions = Array.from(
  { length: MAX_INSTALLMENTS - MIN_INSTALLMENTS + 1 },
  (_, i) => MIN_INSTALLMENTS + i
);

// Shown inside the Open Invoices section of MyAccount once the member has
// checked 1+ invoices. Lets them pick how many monthly installments to
// split the selected total across, previews the per-installment amount,
// and hands the chosen count back to the parent to actually create the
// plan (POST /api/me/payment-plans) — this component has no API access of
// its own.
export default function PaymentPlanSetup({
  selectedInvoices,
  onSubmit,
  onClear,
  submitting = false,
  error = null,
}) {
  const [installments, setInstallments] = useState(DEFAULT_INSTALLMENTS);

  const total = useMemo(
    () => selectedInvoices.reduce((sum, inv) => sum + (Number(inv.total) || 0), 0),
    [selectedInvoices]
  );

  const preview = useMemo(
    () => computeInstallmentPreview(total, installments),
    [total, installments]
  );

  if (selectedInvoices.length === 0) return null;

  return (
    <Box
      sx={{
        border: '1px solid rgba(36,34,88,0.25)',
        borderRadius: 1,
        background: 'rgba(36,34,88,0.04)',
        p: { xs: 2.5, md: 3 },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
      >
        <Box>
          <Typography fontWeight={700}>
            {selectedInvoices.length} invoice{selectedInvoices.length > 1 ? 's' : ''} selected —{' '}
            {currency(total)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Set up a payment plan to pay these off in monthly installments instead of all at
            once.
          </Typography>
        </Box>
        <Button size="small" onClick={onClear}>
          Clear selection
        </Button>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" mt={3}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="installments-label">Number of installments</InputLabel>
          <Select
            labelId="installments-label"
            label="Number of installments"
            value={installments}
            onChange={(e) => setInstallments(Number(e.target.value))}
          >
            {installmentOptions.map((n) => (
              <MenuItem key={n} value={n}>
                {n} months
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {preview ? (
          <Typography>
            {currency(preview.installmentAmount)} / month for {installments} months
          </Typography>
        ) : (
          <Typography color="error" variant="body2">
            Choose fewer installments — the amount per installment is too small.
          </Typography>
        )}

        <Button
          variant="contained"
          disabled={!preview || submitting}
          onClick={() => onSubmit(installments)}
          sx={{
            bgcolor: '#242258',
            borderRadius: '60px',
            px: 3,
            '&:hover': { bgcolor: '#1a1840' },
          }}
        >
          {submitting ? 'Setting up…' : 'Set up payment plan'}
        </Button>
      </Stack>

      {preview && Math.abs(preview.roundingDifference) > 0 && (
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          Total charged over the plan: {currency(preview.totalCharged)} (rounds to the nearest
          cent per installment).
        </Typography>
      )}

      {error && (
        <Typography color="error" mt={2}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
